import {
  type QueryKey,
  type UseQueryOptions,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import { getDeferredResourcePollDelay } from "@/lib/get-deferred-resource-poll-delay";
import type { DeferredResource } from "@garimpa/api/deferredResource/types";

interface UseDeferredResourcePollingInput<T> {
  resource: DeferredResource<T> | null | undefined;
  queryUpdatedAt: number;
  queryKey: QueryKey;
  compute: () => Promise<DeferredResource<T> | undefined>;
  refetch: () => Promise<unknown>;
  maxWaitMs?: number;
}

type DeferredResourceQueryOptions<T> = UseQueryOptions<
  DeferredResource<T>,
  Error,
  DeferredResource<T>,
  QueryKey
> & {
  queryKey: QueryKey;
};

interface UseDeferredResourceQueryInput<T> {
  queryOptions: DeferredResourceQueryOptions<T>;
  compute: () => Promise<DeferredResource<T> | undefined>;
  maxWaitMs?: number;
}

const DEFAULT_MAX_WAIT_MS = 60_000;

function resolveDeferredResourceValue<T>(
  resource: DeferredResource<T> | null | undefined,
): T | null {
  if (!resource) return null;
  if (resource.status === "ready") return resource.value;
  return resource.fallback;
}

/**
 * Starts deferred resource computation once and polls the cache-only read query.
 */
function useDeferredResourcePolling<T>({
  resource,
  queryUpdatedAt,
  queryKey,
  compute,
  refetch,
  maxWaitMs = DEFAULT_MAX_WAIT_MS,
}: UseDeferredResourcePollingInput<T>) {
  const queryClient = useQueryClient();
  const requestedResourceKeyRef = useRef<string | null>(null);
  const requestedComputeKeyRef = useRef<string | null>(null);
  const startedAtRef = useRef<number | null>(null);
  const pollAttemptRef = useRef(0);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (!resource || (resource.status !== "pending" && resource.status !== "error")) {
      pollAttemptRef.current = 0;
      return;
    }

    const resourceKey = resource.resourceKey;
    if (requestedResourceKeyRef.current !== resourceKey) {
      requestedResourceKeyRef.current = resourceKey;
      requestedComputeKeyRef.current = null;
      startedAtRef.current = Date.now();
      pollAttemptRef.current = 0;
    }

    let cancelled = false;

    const scheduleRefetch = (pollAfterMs: number) => {
      const startedAt = startedAtRef.current ?? Date.now();
      const remainingWaitMs = maxWaitMs - (Date.now() - startedAt);
      if (remainingWaitMs <= 0) return;

      const pollDelayMs = Math.min(
        getDeferredResourcePollDelay({
          initialDelayMs: pollAfterMs,
          attempt: pollAttemptRef.current,
        }),
        remainingWaitMs,
      );
      pollAttemptRef.current += 1;

      timerRef.current = window.setTimeout(() => {
        if (!cancelled) void refetch();
      }, pollDelayMs);
    };

    const run = async () => {
      // On error, always retry compute — skip deduplication so the lease can reset the job.
      // On pending, skip compute if already in-flight or the server-side job is running.
      if (
        resource.status !== "error" &&
        (requestedComputeKeyRef.current === resourceKey ||
          (requestedResourceKeyRef.current === resourceKey && resource.reason === "running"))
      ) {
        scheduleRefetch(resource.pollAfterMs);
        return;
      }

      requestedComputeKeyRef.current = resourceKey;

      let computed: DeferredResource<T> | undefined;
      try {
        computed = await compute();
      } catch {
        if (!cancelled) scheduleRefetch(resource.pollAfterMs);
        return;
      }

      if (cancelled) return;
      if (!computed) return;
      if (computed.status !== "pending") {
        queryClient.setQueryData(queryKey, computed);
        return;
      }

      scheduleRefetch(computed.pollAfterMs);
    };

    void run();

    return () => {
      cancelled = true;
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [resource, queryUpdatedAt, queryKey, compute, refetch, maxWaitMs, queryClient]);
}

export function useDeferredResourceQuery<T>({
  queryOptions,
  compute,
  maxWaitMs,
}: UseDeferredResourceQueryInput<T>) {
  const shouldRunQuery = queryOptions.enabled !== false;
  const query = useQuery({
    ...queryOptions,
    enabled: shouldRunQuery,
  });
  const resource = query.data;

  useDeferredResourcePolling({
    resource,
    queryUpdatedAt: Math.max(query.dataUpdatedAt, query.errorUpdatedAt),
    queryKey: queryOptions.queryKey,
    compute,
    refetch: query.refetch,
    maxWaitMs,
  });

  return {
    query,
    resource,
    value: resolveDeferredResourceValue(resource),
  };
}
